<script setup>
import { ref, watch, onMounted } from 'vue';
import { useFunctionGetter } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PrestaShopAPI from '../../../api/integrations/prestashop';
import PrestaShopOrderItem from './PrestaShopOrderItem.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
});

const contact = useFunctionGetter('contacts/getContact', props.contactId);

const orders = ref([]);
const loading = ref(false);
const error = ref('');
const hooks = ref([]);
const hasFetched = ref(false);

// Manual search state
const searchQuery = ref('');
const searchType = ref('reference');
const searching = ref(false);
const searchError = ref('');
const searchResults = ref([]);
const showSearch = ref(false);

const fetchOrders = async () => {
  loading.value = true;
  error.value = '';
  try {
    const response = await PrestaShopAPI.getContactOrders(props.contactId);
    orders.value = response.data.orders || [];
    hooks.value = response.data.hooks || [];
    hasFetched.value = true;
  } catch (e) {
    error.value = e.response?.data?.error || 'Error al cargar pedidos';
    hasFetched.value = true;
  } finally {
    loading.value = false;
  }
};

const manualSearch = async () => {
  const query = searchQuery.value.trim();
  if (!query || !hooks.value.length) return;

  searching.value = true;
  searchError.value = '';
  searchResults.value = [];

  try {
    const allResults = [];
    for (const hook of hooks.value) {
      try {
        const { data } = await PrestaShopAPI.searchOrders(
          hook.id,
          query,
          searchType.value
        );
        if (data.orders && data.orders.length) {
          data.orders.forEach(order => {
            order.store_name = hook.store_name;
            order.hook_id = hook.id;
          });
          allResults.push(...data.orders);
        }
      } catch {
        // Skip failed stores
      }
    }
    searchResults.value = allResults;
    if (!allResults.length) {
      searchError.value = 'No se encontraron pedidos';
    }
  } catch (e) {
    searchError.value = 'Error en la busqueda';
  } finally {
    searching.value = false;
  }
};

// Reset state when contact changes
watch(
  () => props.contactId,
  () => {
    orders.value = [];
    loading.value = false;
    error.value = '';
    hasFetched.value = false;
    searchQuery.value = '';
    searchResults.value = [];
    searchError.value = '';
    showSearch.value = false;
  }
);

// Load hooks on mount (lightweight, no order search)
onMounted(async () => {
  try {
    const { data } = await PrestaShopAPI.getStores();
    hooks.value = (data.stores || []).map(s => ({ id: s.id, store_name: s.store_name }));
  } catch {
    // Ignore - hooks will be loaded when fetching orders
  }
});
</script>

<template>
  <div class="px-4 py-2 text-n-slate-12">
    <!-- Initial state: button to search orders -->
    <div v-if="!hasFetched && !loading" class="flex flex-col items-center gap-2 py-2">
      <Button
        faded
        blue
        sm
        label="Buscar pedidos"
        @click="fetchOrders"
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center p-4">
      <Spinner size="32" class="text-n-brand" />
    </div>

    <!-- Error -->
    <div v-if="error && !loading" class="text-center text-sm text-n-ruby-11 py-2">
      {{ error }}
    </div>

    <!-- Results after fetch -->
    <template v-if="hasFetched && !loading && !error">
      <div v-if="orders.length">
        <PrestaShopOrderItem
          v-for="order in orders"
          :key="`${order.hook_id}-${order.id}`"
          :order="order"
          :contact-id="contactId"
          @contact-synced="fetchOrders"
        />
      </div>
      <div v-else class="text-center text-sm text-n-slate-10 py-2">
        No se encontraron pedidos.
      </div>

      <!-- Manual search section -->
      <div class="mt-3 pt-3 border-t border-n-weak">
        <button
          v-if="!showSearch"
          class="text-xs text-n-brand cursor-pointer hover:underline w-full text-center"
          @click="showSearch = true"
        >
          Buscar pedido manualmente
        </button>
        <div v-else class="flex flex-col gap-2">
          <!-- Search type tabs -->
          <div class="flex gap-0.5 bg-n-alpha-3 rounded p-0.5">
            <button
              v-for="opt in [
                { value: 'reference', label: 'Ref' },
                { value: 'order_id', label: 'ID' },
                { value: 'email', label: 'Email' },
                { value: 'phone', label: 'Tel' },
              ]"
              :key="opt.value"
              class="flex-1 text-xs py-1 rounded text-center transition-colors"
              :class="searchType === opt.value
                ? 'bg-n-solid-2 text-n-slate-12 shadow-sm font-medium'
                : 'text-n-slate-10 hover:text-n-slate-12'"
              @click="searchType = opt.value"
            >
              {{ opt.label }}
            </button>
          </div>
          <!-- Search input -->
          <div class="flex gap-1.5">
            <input
              v-model="searchQuery"
              type="text"
              class="flex-1 text-xs bg-n-solid-2 border border-n-weak rounded px-2.5 py-1.5 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-blue-7"
              :placeholder="
                searchType === 'reference'
                  ? 'ABCDEFGH'
                  : searchType === 'order_id'
                    ? '12345'
                    : searchType === 'email'
                      ? 'email@ejemplo.com'
                      : '+34600000000'
              "
              @keyup.enter="manualSearch"
            />
            <button
              class="text-xs px-3 py-1.5 rounded bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-40 transition-colors whitespace-nowrap"
              :disabled="searching || !searchQuery.trim()"
              @click="manualSearch"
            >
              {{ searching ? '...' : 'Buscar' }}
            </button>
          </div>

          <!-- Search loading -->
          <div v-if="searching" class="flex justify-center py-2">
            <Spinner size="20" class="text-n-brand" />
          </div>

          <!-- Search error -->
          <div
            v-if="searchError && !searching"
            class="text-xs text-n-slate-10 text-center py-1"
          >
            {{ searchError }}
          </div>

          <!-- Search results -->
          <div v-if="searchResults.length">
            <PrestaShopOrderItem
              v-for="order in searchResults"
              :key="`search-${order.hook_id}-${order.id}`"
              :order="order"
              :contact-id="contactId"
              @contact-synced="fetchOrders"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
